import * as React from 'react';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {Button} from 'shells/button/button';
import {DynamicStatusButton} from 'shells/button/dynamic-status-button/dynamic-status-button';
import {ButtonGroup} from 'shells/button-group/button-group';
// @ts-expect-error - imported from .js/.jsx file
import {CogIcon} from 'shells/icon/svg/cog-icon';
import {MainContentContainer} from 'shells/layout/main-content-container';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {LoadingText} from 'shells/loading-placeholder/loading-text';

import type {ExternalServiceType} from 'nutshell-graphql-types';
import type {GetSessionUser_loggedInUser_phoneIntegrations as PhoneIntegration} from 'nutshell-graphql-types';

import {appHistory} from '../../history';
import {usePoll} from '../../util/hooks';

import {useGetSessionUser} from '../core/use-get-session-user';
import {MeetingsTableWrapper} from '../external-meetings/tables/meetings-table-wrapper';
import {PageTitleBar} from '../layout/page-title-bar';

import {getFlashBannerType, getStatusDescription, getStatusText} from './helpers';
import {PhoneIntegrationsStatusPopover} from './phone-integrations-status-popover';

type Props = ContextRouter;

export function CallsListPage(props: Props) {
    const [isDialogOpen, setIsDialogOpen] = React.useState<boolean>(false);

    const {user, isLoading, errorMessage, startPolling, stopPolling} = useGetSessionUser();

    const phoneIntegrations: PhoneIntegration[] = user ? user.phoneIntegrations : [];
    const enabledPhoneIntegrations: PhoneIntegration[] = phoneIntegrations.filter(
        (integration) => integration.isEnabledForUser === true
    );
    const enabledIntegrationTypes: ExternalServiceType[] = enabledPhoneIntegrations.map(
        (integration) => integration.integrationType
    );

    const hasEnabledIntegrations = enabledIntegrationTypes.length > 0;

    const isSyncing = enabledPhoneIntegrations.some(
        (integration) => integration && integration.isPerformingInitialSync === true
    );

    usePoll({
        shouldPoll: isSyncing,
        startPolling,
        interval: 5000,
        stopPolling,
    });

    return (
        <MainContentContainer whiteBackground={true}>
            <PageTitleBar
                title='Calls'
                subheaderPrimaryText={'Sync and summarize calls with Nutshell'}
                buttons={
                    <ButtonGroup>
                        {isLoading && !isSyncing ? (
                            <LoadingText height={30} width={170} />
                        ) : (
                            <DynamicStatusButton
                                key='phone-integrations'
                                popoverLocation='left'
                                text={
                                    isSyncing
                                        ? 'Syncing...'
                                        : getStatusText(enabledIntegrationTypes, errorMessage)
                                }
                                headerType={getFlashBannerType(
                                    hasEnabledIntegrations,
                                    errorMessage
                                )}
                                headerContent={getStatusDescription(
                                    enabledIntegrationTypes,
                                    errorMessage
                                )}
                                isDialogOpen={isDialogOpen}
                                setIsDialogOpen={setIsDialogOpen}
                                popoverContent={
                                    <PhoneIntegrationsStatusPopover
                                        phoneIntegrations={enabledPhoneIntegrations}
                                        onClose={() => setIsDialogOpen(false)}
                                    />
                                }
                            />
                        )}
                        <Button
                            variant='secondary'
                            size='big'
                            icon={CogIcon}
                            onClick={() => {
                                appHistory.push('/my-account/phone');
                            }}
                        >
                            Phone settings
                        </Button>
                    </ButtonGroup>
                }
            />
            {isLoading && !isSyncing ? (
                <LoadingPage />
            ) : (
                <MeetingsTableWrapper
                    {...props}
                    isPerformingInitialSync={isSyncing}
                    firstEnabledIntegrationType={
                        enabledPhoneIntegrations[0] && enabledPhoneIntegrations[0].integrationType
                    }
                    serviceType='PHONES'
                />
            )}
        </MainContentContainer>
    );
}
